import { Typography } from "@mui/material";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { FlexColBox } from "../../../common/components";
import { palette, spacing } from "../../../styles/theme";
import { ComboItems, Country, ProductLineType } from "../../../types";
import { ItemPricing } from "../../../types";
import { useCurrency } from "../../../common/hooks/useCurrency";
import { FormContext } from "../../../context/Form";

interface Props {
  deprecatedDesign: boolean;
  item: ItemPricing;
  comboItems: ComboItems;
  hideEndOfLifeWarning?: boolean;
  forCartItem?: boolean;
}

const CartItemWarnings = ({
  deprecatedDesign,
  item,
  comboItems,
  hideEndOfLifeWarning,
  forCartItem,
}: Props) => {
  const {
    aboveMinimumQuantity,
    belowMaximumQuantity,
    quantityRequirements: comboItemQuantityRequirements,
    quantity,
  } = comboItems;

  const { productName } = item;
  const { t } = useTranslation();
  const { getCurrency } = useCurrency();

  const isSocks = productName.toUpperCase().includes("SOCKS");

  const { formik } = useContext(FormContext);
  const country = formik?.values?.shippingAddress?.country as Country;

  const notAvailableInUS =
    productName.toUpperCase().includes(ProductLineType.CLASSIC) &&
    (getCurrency() === "usd" || country === "US");

  const shouldShowProductVersionNeedUpgrade = item.productVersionNeedUpgrade;
  const shouldShowDeprecatedDesignForCartItem =
    item.deprecatedDesign &&
    forCartItem &&
    !shouldShowProductVersionNeedUpgrade;
  const shouldShowColorsEndOfLife =
    item.containsColorsEndOfLife &&
    !hideEndOfLifeWarning &&
    !shouldShowProductVersionNeedUpgrade;
  const shouldShowQuantityOnly =
    !deprecatedDesign &&
    aboveMinimumQuantity &&
    !belowMaximumQuantity &&
    !forCartItem;
  const shouldShowModifyDesign = deprecatedDesign && !forCartItem;
  const shouldShowMinimumQuantity =
    !deprecatedDesign && !aboveMinimumQuantity && !forCartItem;
  const shouldShowMinimumQuantityPerSize =
    !notAvailableInUS &&
    !deprecatedDesign &&
    !item.minimumQuantityPerSizeReached &&
    !forCartItem;
  const shouldShowNotAvailableInUS = notAvailableInUS;

  const hasAnyWarnings =
    shouldShowDeprecatedDesignForCartItem ||
    shouldShowColorsEndOfLife ||
    shouldShowProductVersionNeedUpgrade ||
    shouldShowQuantityOnly ||
    shouldShowModifyDesign ||
    shouldShowMinimumQuantity ||
    shouldShowMinimumQuantityPerSize ||
    shouldShowNotAvailableInUS;

  if (!hasAnyWarnings) {
    return null;
  }

  return (
    <FlexColBox gap={{ xs: spacing.xxsMobile, lg: spacing.xxsDesktop }}>
      {shouldShowDeprecatedDesignForCartItem && (
        <Typography variant="textSm" color="error">
          {t("cart.warnings.styleOrColorNoLongerAvailable")}
        </Typography>
      )}
      {shouldShowColorsEndOfLife && (
        <Typography variant="textSm" color={palette.misc.green}>
          {t("cart.warnings.colorEndOfLife")}
        </Typography>
      )}
      {shouldShowProductVersionNeedUpgrade && (
        <Typography variant="textSm" color="error">
          {t("generator.warnings.productVersionNeedUpgrade")}
        </Typography>
      )}
      {shouldShowQuantityOnly && (
        <Typography variant="textSm" color={palette.grey.primary}>
          {t(`checkout.quantityOnly.${isSocks ? "socks" : "others"}`, {
            count: quantity,
          })}
        </Typography>
      )}
      {shouldShowModifyDesign && (
        <Typography variant="textSm" color="error">
          {t("cart.warnings.modifyDesign")}
        </Typography>
      )}
      {shouldShowMinimumQuantity && (
        <Typography variant="textSm" color={palette.red.error}>
          {t(`checkout.minimum_qty.${isSocks ? "socks" : "others"}`, {
            minQty: comboItemQuantityRequirements.minimumQuantity,
          })}
        </Typography>
      )}
      {shouldShowMinimumQuantityPerSize && (
        <Typography variant="textSm" color={palette.red.error}>
          {t(`checkout.minimum_qty_per_size.${isSocks ? "socks" : "others"}`, {
            minQtyPerSize: item.quantityRequirements.minimumQuantityPerSize,
          })}
        </Typography>
      )}
      {shouldShowNotAvailableInUS && (
        <Typography variant="textSm" color={palette.grey.primary}>
          {t(`pricing.notAvailableInCountry`, {
            country: "US",
          })}
        </Typography>
      )}
    </FlexColBox>
  );
};

export default CartItemWarnings;
